import { StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import Svg, { Circle, Line } from 'react-native-svg'
import { COLORS, RADIUS, SPACING, TYPOGRAPHY } from '../../constants/theme'
import {
    SHOT_MAX_MS,
    ShotClockState,
    ShotClockTone,
    ShotPlayer,
    formatSeconds,
} from '../../utils/shotClockUtils'

interface Labels {
    reset: string
    extend: string
    switchPlayer: string
    start: string
    pause: string
    resume: string
    expired: string
    extAvailable: string
    soundOn: string
    soundOff: string
    timeout: string
    timeoutEnd: string
}

interface Props {
    player1Name: string
    player2Name: string
    state: ShotClockState
    seconds: number
    remainingMs: number
    tone: ShotClockTone
    muted: boolean
    showKeyHints: boolean
    labels: Labels
    timeoutActive: boolean
    timeoutBusy: boolean
    onTimeout: () => void
    onToggle: () => void
    onReset: () => void
    onExtend: () => void
    onSwitch: (to?: ShotPlayer) => void
    onToggleMute: () => void
}

const toneColor = (tone: ShotClockTone): string => {
    if (tone === 'critical') return COLORS.scoreboard.clockCritical
    if (tone === 'warning') return COLORS.scoreboard.clockWarning
    return COLORS.scoreboard.gold
}

/** Small clock icon shown after a player's name while the one-time +30s is unused. */
export const ExtensionClockIcon = ({ size = 18 }: { size?: number }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24">
        <Circle cx={12} cy={12} r={9} stroke={COLORS.scoreboard.gold} strokeWidth={2} fill="none" />
        <Line x1={12} y1={12} x2={12} y2={7} stroke={COLORS.scoreboard.gold} strokeWidth={2} strokeLinecap="round" />
        <Line x1={12} y1={12} x2={16} y2={14} stroke={COLORS.scoreboard.gold} strokeWidth={2} strokeLinecap="round" />
    </Svg>
)

/** Stopwatch pictogram for the extended-mode toggle (SVG only, safe on iOS 15). */
export const StopwatchIcon = ({ size = 18, color = COLORS.scoreboard.goldDim }: { size?: number; color?: string }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24">
        <Circle cx={12} cy={13} r={8} stroke={color} strokeWidth={2} fill="none" />
        <Line x1={12} y1={13} x2={12} y2={9} stroke={color} strokeWidth={2} strokeLinecap="round" />
        <Line x1={9} y1={2} x2={15} y2={2} stroke={color} strokeWidth={2} strokeLinecap="round" />
        <Line x1={12} y1={2} x2={12} y2={5} stroke={color} strokeWidth={2} strokeLinecap="round" />
    </Svg>
)

const ShotClockBand = ({
    player1Name,
    player2Name,
    state,
    seconds,
    remainingMs,
    tone,
    muted,
    showKeyHints,
    labels,
    timeoutActive,
    timeoutBusy,
    onTimeout,
    onToggle,
    onReset,
    onExtend,
    onSwitch,
    onToggleMute,
}: Props) => {
    const color = toneColor(tone)
    const expired = state.status === 'expired'
    const progress = Math.max(0, Math.min(1, remainingMs / SHOT_MAX_MS))
    const toggleLabel = state.status === 'idle'
        ? labels.start
        : state.status === 'running' ? labels.pause : labels.resume
    const extDisabled = !state.extAvailable[state.activePlayer - 1]
        || state.status === 'idle' || state.status === 'expired'

    const playerChip = (player: ShotPlayer, name: string) => {
        const active = state.activePlayer === player
        const extAvailable = state.extAvailable[player - 1]
        return (
            <TouchableOpacity
                style={[styles.chip, active && styles.chipActive]}
                onPress={() => { if (!active) onSwitch(player) }}
                activeOpacity={active ? 1 : 0.7}
            >
                <View style={styles.chipNameRow}>
                    <Text
                        style={[styles.chipName, active && styles.chipNameActive]}
                        numberOfLines={1}
                    >
                        {name.toUpperCase()}
                    </Text>
                    {extAvailable && (
                        <View accessibilityLabel={labels.extAvailable}>
                            <ExtensionClockIcon />
                        </View>
                    )}
                </View>
            </TouchableOpacity>
        )
    }

    return (
        <View style={[styles.band, expired && styles.bandExpired]}>
            <View style={styles.topRow}>
                {playerChip(1, player1Name)}
                <TouchableOpacity style={styles.digitsWrap} onPress={onToggle} activeOpacity={0.8}>
                    <Text style={[styles.digits, { color }]}>
                        {formatSeconds(seconds)}
                    </Text>
                    {expired && <Text style={styles.expiredText}>{labels.expired}</Text>}
                </TouchableOpacity>
                {playerChip(2, player2Name)}
            </View>

            <View style={styles.progressBg}>
                <View style={[styles.progressFill, { width: `${progress * 100}%`, backgroundColor: color }]} />
            </View>

            <View style={styles.buttonsRow}>
                <TouchableOpacity style={styles.btn} onPress={onToggle}>
                    <Text style={styles.btnText}>{toggleLabel}</Text>
                    {showKeyHints && <Text style={styles.keyHint}>P</Text>}
                </TouchableOpacity>
                <TouchableOpacity style={styles.btn} onPress={onReset}>
                    <Text style={styles.btnText}>↺ {labels.reset}</Text>
                    {showKeyHints && <Text style={styles.keyHint}>SPACE</Text>}
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.btn, extDisabled && styles.btnDisabled]}
                    onPress={onExtend}
                    disabled={extDisabled}
                >
                    <Text style={styles.btnText}>{labels.extend}</Text>
                    {showKeyHints && <Text style={styles.keyHint}>E</Text>}
                </TouchableOpacity>
                <TouchableOpacity style={styles.btnPrimary} onPress={() => onSwitch()}>
                    <Text style={styles.btnPrimaryText}>⇄ {labels.switchPlayer}</Text>
                    {showKeyHints && <Text style={styles.keyHint}>ENTER</Text>}
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.btn, timeoutActive && styles.btnActive, timeoutBusy && styles.btnDisabled]}
                    onPress={onTimeout}
                    disabled={timeoutBusy}
                >
                    <Text style={styles.btnText}>{timeoutActive ? labels.timeoutEnd : labels.timeout}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.btnSmall} onPress={onToggleMute}>
                    <Text style={styles.btnText}>{muted ? labels.soundOff : labels.soundOn}</Text>
                    {showKeyHints && <Text style={styles.keyHint}>M</Text>}
                </TouchableOpacity>
            </View>
        </View>
    )
}

const MONO = 'Menlo, Consolas, "SF Mono", "Courier New", monospace'

const styles = StyleSheet.create({
    band: {
        backgroundColor: COLORS.scoreboard.panelBg,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
        paddingVertical: SPACING.md,
        paddingHorizontal: SPACING.lg,
        gap: SPACING.sm,
    },
    bandExpired: {
        borderColor: COLORS.scoreboard.clockCritical,
        backgroundColor: COLORS.scoreboard.clockCriticalBg,
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
    },
    chip: {
        flex: 1,
        paddingVertical: SPACING.sm,
        paddingHorizontal: SPACING.md,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
        alignItems: 'center',
    },
    chipActive: {
        borderColor: COLORS.scoreboard.gold,
        borderWidth: 2,
    },
    chipNameRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
    },
    chipName: {
        color: COLORS.scoreboard.goldDim,
        fontSize: TYPOGRAPHY.fontSize.lg,
        fontWeight: '400',
        letterSpacing: 1,
        flexShrink: 1,
    },
    chipNameActive: {
        color: COLORS.scoreboard.gold,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
    },
    digitsWrap: {
        minWidth: 140,
        alignItems: 'center',
    },
    digits: {
        fontFamily: MONO,
        fontSize: 72,
        lineHeight: 76,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        fontVariant: ['tabular-nums'],
        letterSpacing: 2,
    },
    expiredText: {
        color: COLORS.scoreboard.clockCritical,
        fontSize: TYPOGRAPHY.fontSize.sm,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        letterSpacing: 3,
    },
    progressBg: {
        height: 6,
        borderRadius: 3,
        backgroundColor: COLORS.scoreboard.background,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
    },
    buttonsRow: {
        flexDirection: 'row',
        gap: SPACING.sm,
    },
    btn: {
        flex: 1,
        height: 52,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
        backgroundColor: COLORS.scoreboard.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnSmall: {
        width: 96,
        height: 52,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.divider,
        backgroundColor: COLORS.scoreboard.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnPrimary: {
        flex: 1.4,
        height: 52,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.scoreboard.gold,
        backgroundColor: COLORS.scoreboard.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnActive: {
        borderColor: COLORS.scoreboard.gold,
        borderWidth: 2,
    },
    btnDisabled: {
        opacity: 0.35,
    },
    btnText: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize.md,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
    },
    btnPrimaryText: {
        color: COLORS.scoreboard.gold,
        fontSize: TYPOGRAPHY.fontSize.md,
        fontWeight: TYPOGRAPHY.fontWeight.bold,
        letterSpacing: 1,
    },
    keyHint: {
        color: COLORS.scoreboard.goldDim,
        fontSize: 10,
        fontFamily: MONO,
    },
})

export default ShotClockBand
